import styled, { keyframes } from 'styled-components';
import backgroundImg from '../../imgs/me.jpeg';
import { color, font, displaySoft, label, space, maxWidth, bp, ease } from '../../styles/tokens';

const rise = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: none; }
`;

// The logo's sparkle catches the light once, on arrival. Nothing loops.
const twinkle = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(0.8) rotate(0deg); }
  50%      { opacity: 1;    transform: scale(1.15) rotate(45deg); }
`;

export const Container = styled.header`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  padding: 0 ${space.gutter} clamp(48px, 8vh, 88px);
  overflow: hidden;
  background-color: ${color.ink};
  background-image: url(${backgroundImg});
  background-position: 50% 22%;
  background-repeat: no-repeat;
  background-size: cover;

  ${bp.md} {
    background-position: 50% 28%;
    align-items: center;
  }

  /* A neutral plum scrim rather than a colour wash — it holds the type without
     dulling the fuchsia in the portrait. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(32, 26, 30, 0.5) 0%,
      rgba(32, 26, 30, 0.18) 32%,
      rgba(32, 26, 30, 0.78) 72%,
      ${color.ink} 100%
    );
  }
`;

export const Content = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${maxWidth};
  margin: 0 auto;

  > * {
    animation: ${rise} 0.9s ${ease} both;
  }

  > *:nth-child(1) { animation-delay: 0.05s; }
  > *:nth-child(2) { animation-delay: 0.2s; }
  > *:nth-child(3) { animation-delay: 0.35s; }
  > *:nth-child(4) { animation-delay: 0.5s; }
`;

export const Eyebrow = styled.p`
  ${label};
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${color.rose};
  margin-bottom: 20px;

  svg {
    animation: ${twinkle} 2.4s ${ease} 1.1s 1 both;
  }
`;

export const Wordmark = styled.h1`
  ${displaySoft};
  color: ${color.white};
  font-size: clamp(58px, 15vw, 148px);
  font-style: italic;
  /* Enough leading for the italic descenders to clear the line below. */
  line-height: 1.02;
  margin-bottom: 4px;

  /* "studio" sits under the name in tracked caps, mirroring the logo lockup. */
  span {
    display: block;
    ${label};
    font-style: normal;
    font-size: clamp(12px, 2.4vw, 20px);
    letter-spacing: 0.55em;
    color: ${color.rose};
    padding-left: 0.35em;
    /* Scales with the wordmark so the italic "p" tail always clears the caps. */
    margin-top: clamp(10px, 3.4vw, 36px);
  }
`;

export const Tagline = styled.p`
  max-width: 34ch;
  margin-top: clamp(22px, 3vw, 30px);
  font-size: clamp(15px, 1.8vw, 18px);
  font-weight: 300;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.82);
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: clamp(28px, 4vw, 40px);
`;

// Persistent booking button. It follows the page rather than pulsing at it.
export const Float = styled.a`
  position: fixed;
  right: clamp(16px, 3vw, 28px);
  bottom: clamp(16px, 3vw, 28px);
  z-index: 60;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 20px;
  border-radius: 999px;
  background: ${color.fuchsia};
  color: ${color.white};
  font-family: ${font.body};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.02em;
  box-shadow: 0 10px 30px rgba(32, 26, 30, 0.28);
  transition: background 0.3s ${ease}, transform 0.3s ${ease};

  svg {
    font-size: 22px;
  }

  span {
    display: none;

    ${bp.sm} {
      display: inline;
    }
  }

  &:hover {
    background: ${color.fuchsiaDark};
    transform: translateY(-3px);
  }
`;

export const ScrollCue = styled.a`
  ${label};
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  display: none;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.3s ${ease};

  &::after {
    content: '';
    width: 1px;
    height: 26px;
    background: linear-gradient(${color.rose}, transparent);
  }

  &:hover {
    color: ${color.rose};
  }

  ${bp.lg} {
    display: flex;
    flex-direction: column;
  }
`;
